import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { updateUserPreference } from '@kirin/api-client/api/account';
import {
  UserPreferenceKey,
  type UserPreference,
  type UserPreferencesResponse,
} from '@kirin/api-client/contracts/user-preferences';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/hooks/api/queryKeys';
import { trackEvent } from '@/lib/analytics';
import { mergePreference } from '@/lib/preferences/mergePreference';
import type { ThemePreferenceType } from '@/styles/types';

interface ThemeMutationContext {
  previous: UserPreferencesResponse | undefined;
  from: ThemePreferenceType;
}

// Optimistic via onMutate: cache is the single source of truth. No rollback on error — `ThemeSyncBadge` + retry surface the failure.
export function useUpdateThemeMutation(
  currentTheme: ThemePreferenceType
): UseMutationResult<
  UserPreference,
  Error,
  ThemePreferenceType,
  ThemeMutationContext
> {
  const queryClient = useQueryClient();

  return useMutation<
    UserPreference,
    Error,
    ThemePreferenceType,
    ThemeMutationContext
  >({
    mutationFn: (next: ThemePreferenceType) =>
      updateUserPreference(apiClient, {
        key: UserPreferenceKey.Theme,
        value: next,
      }),
    // Cancel any in-flight boot GET so its (potentially stale) response cannot overwrite our optimistic write.
    onMutate: async next => {
      await queryClient.cancelQueries({ queryKey: queryKeys.preferences.all });
      const previous = queryClient.getQueryData<UserPreferencesResponse>(
        queryKeys.preferences.all
      );
      const optimistic: UserPreference = {
        key: UserPreferenceKey.Theme,
        value: next,
      };
      queryClient.setQueryData<UserPreferencesResponse>(
        queryKeys.preferences.all,
        old => mergePreference(old, optimistic)
      );
      // Snapshot `from` here — by `onSuccess` the optimistic write has re-rendered
      // ThemeProvider and refreshed the `currentTheme` closure to the new value.
      return { previous, from: currentTheme };
    },
    onSuccess: (updated, _variables, context) => {
      // Reconcile with the server's authoritative value (usually identical to the optimistic write).
      queryClient.setQueryData<UserPreferencesResponse>(
        queryKeys.preferences.all,
        old => mergePreference(old, updated)
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.preferences.all });
      if (updated.key === UserPreferenceKey.Theme && context) {
        trackEvent('preferences_theme_changed', {
          from: context.from,
          to: updated.value,
        });
      }
    },
    onError: err => {
      Sentry.captureException(err, {
        level: 'error',
        tags: { source: 'preferences-mutate' },
      });
    },
    retry: false,
  });
}
